import React, {useEffect} from 'react';
import {hydrateRoot} from 'react-dom/client';
import {Provider} from 'react-redux';
import {ConnectedRouter} from 'connected-react-router';
import {cookieStore} from 'libs/storage/storage';
import storeEngine from 'store/src/store-engine';
import sessionStorage from 'store/storages/sessionStorage';
import ttiPolyfill from 'tti-polyfill';
import {
    callObserversForAnalytics,
    createPaintObserver,
    createServerLatencyObserver,
    pushDataLayer,
    createGAEvent,
    sendWebVitalsResults,
    PERFORMANCE,
} from '@optimaxdev/analytics/mobile';
import '@fontsource/roboto/latin-300-italic.css';
import '@fontsource/roboto/latin-400-italic.css';
import '@fontsource/roboto/latin-700-italic.css';
import {getCLS, getFID, getLCP, getTTFB} from 'web-vitals';
import {loadableReady} from '@loadable/component';
import {ErrorBoundary, withProfiler} from '@sentry/react';
import {QueryClient, QueryClientProvider} from 'react-query';
import {ReactQueryDevtools} from 'react-query/devtools';
import {Hydrate} from 'react-query/hydration';
import 'libs/interceptors/dyInterceptor';
import 'libs/interceptors/interceptors';
import 'libs/interceptors/clientIpInterceptor';
import {initSentry} from 'libs/sentry/sentry';
import {getVisitorId} from 'libs/user/getVisitorId';
import {AppCrashFallback} from 'components/appCrashFallback';
import App from 'containers/app/app';
import {polyfillsInject} from 'libs/polyfillsInject';
import {DyDataProvider} from 'features/experiments/dyDataContext';
import {setCustomProperties} from 'libs/roxCustomSetup';
import {initRollout} from 'libs/rolloutConfiguration/rolloutConfiguration';
import {CookiesProvider} from 'libs/cookie/cookiesProvider';
import configureStore, {history} from 'libs/configureStore';
import {getLandingUrl, getLandingPage} from 'libs/hto';

import '../css/roboto-fonts-normal.scss';
import '../css/base.scss';
import {setTimeStampCookies} from './initialCookies';

let store;
let persistor;
const sessionStore = storeEngine.createStore([sessionStorage]);
const saveState = () => {
    const sessionStorageState = {
        activeSort: store.getState().activeSort,
    };

    sessionStore.set('state', sessionStorageState);
};

try {
    // eslint-disable-next-line no-underscore-dangle
    const preloadedState = window.__APP_STATE || {};
    const persistedState = sessionStore.get('state');
    ({store, persistor} = configureStore({...preloadedState, ...persistedState}));
    store.subscribe(saveState);
} catch (e) {
    ({store, persistor} = configureStore({}));
}

/**
 * Wait for persistor has synced with redux store before React components hydration
 * Used as SSR-friendly alternative of <PersistGate />(redux-persist/integration/react)
 * @returns {Promise} promise of readiness
 */
const persistedStoreReady = () => {
    return new Promise(resolve => {
        if (persistor.getState().bootstrapped) {
            resolve();
        } else {
            persistor.subscribe(() => {
                if (persistor.getState().bootstrapped) {
                    resolve();
                }
            });
        }
    });
};

// Polyfill for NodeList.forEach()
// highly important for analytics!
if (window.NodeList && !NodeList.prototype.forEach) {
    NodeList.prototype.forEach = Array.prototype.forEach;
}

initSentry({history});

ttiPolyfill.getFirstConsistentlyInteractive().then(label => {
    if (label)
        pushDataLayer(createGAEvent(PERFORMANCE, {label, name: 'Time to Interactive'}, false));
});

callObserversForAnalytics([createPaintObserver, createServerLatencyObserver]);

getCLS(sendWebVitalsResults);
getFID(sendWebVitalsResults);
getLCP(sendWebVitalsResults);
getTTFB(sendWebVitalsResults);

const rootEl = document.getElementById('react-root');

// eslint-disable-next-line no-underscore-dangle
const dehydratedState = window.__REACT_QUERY_STATE__;
const queryClient = new QueryClient();

initRollout();
setCustomProperties({
    geolocation: cookieStore.get('x-akamai-geo', '').split(','),
    visitorId: getVisitorId(),
    origin: window.location.origin,
    landingUrl: getLandingUrl(),
    landingPage: getLandingPage(store.getState()?.routerInfo?.page),
});

/**
 * Function which starts all initial set cookies functions
 */
const setInitialCookies = () => {
    setTimeStampCookies();
};

/**
 * The application root component
 *
 * @returns {React.Node} - the root react node
 */
export function ApplicationRoot(): React.Node {
    useEffect(() => {
        if (!sessionStore.get('isNewUser') && !cookieStore.get('_dyid')) {
            sessionStore.set('isNewUser', true);
        }

        if (!__NODEJS__) {
            setInitialCookies();
        }

        document.getElementById('react-root').setAttribute('data-hydrated', true);
    }, []);

    return (
        <ErrorBoundary fallback={AppCrashFallback}>
            <QueryClientProvider client={queryClient}>
                <Hydrate state={dehydratedState}>
                    <Provider store={store}>
                        <DyDataProvider>
                            <CookiesProvider>
                                <ConnectedRouter history={history}>
                                    <App />
                                </ConnectedRouter>
                            </CookiesProvider>
                        </DyDataProvider>
                    </Provider>
                </Hydrate>
                <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
        </ErrorBoundary>
    );
}
ApplicationRoot.displayName = 'ApplicationRoot';

const ApplicationRootWithProfiler = withProfiler(ApplicationRoot, {name: 'ApplicationRoot'});

try {
    (async () => {
        await polyfillsInject();
        await persistedStoreReady();

        loadableReady(() => hydrateRoot(rootEl, <ApplicationRootWithProfiler />));
    })();
} catch (error) {
    logWithSentry(error);
}
